export interface ILoadJsOptions {
  id?: string
  async?: boolean
  defer?: boolean
  timeoutMs?: number
  /** Some SDKs expose their API after the script's load event. */
  ready?: () => boolean
}

interface ScriptRequest {
  promise: Promise<Event | string>
  loaded: boolean
  script: HTMLScriptElement
  owned: boolean
}
const requests = new Map<string, ScriptRequest>()

const isReady = (ready: () => boolean) => {
  try { return ready() } catch { return false }
}

export const loadScript = (url: string, options: ILoadJsOptions = {}): Promise<Event | string> => {
  const timeoutMs = options.timeoutMs && options.timeoutMs > 0 ? options.timeoutMs : 15000
  const started = Date.now()
  let absoluteUrl: string
  try { absoluteUrl = new URL(url, document.baseURI).href } catch {
    return Promise.reject(new Error('Invalid script URL'))
  }
  const byId = options.id ? document.getElementById(options.id) as HTMLScriptElement | null : null
  if (byId && (!byId.src || new URL(byId.src, document.baseURI).href !== absoluteUrl)) {
    return Promise.reject(new Error('Script id conflicts with a different URL'))
  }

  let request = requests.get(absoluteUrl)
  if (!request) {
    const existing = byId || Array.from(document.scripts).find(script => new URL(script.src, document.baseURI).href === absoluteUrl)
    const script = existing || document.createElement('script')
    const loaded = script.dataset.kiliScriptState === 'loaded'
      || ['loaded', 'complete'].includes((script as HTMLScriptElement & { readyState?: string }).readyState || '')
      || !!(options.ready && isReady(options.ready))

    request = { loaded, promise: Promise.resolve('existed'), script, owned: !existing }
    requests.set(absoluteUrl, request)
    if (!loaded) {
      const entry = request
      entry.promise = new Promise((resolve, reject) => {
        const cleanup = () => {
          clearTimeout(timer)
          script.removeEventListener('load', onLoad)
          script.removeEventListener('error', onError)
        }
        const onLoad = (event: Event) => {
          cleanup()
          script.dataset.kiliScriptState = 'loaded'
          entry.loaded = true
          resolve(event)
        }
        const fail = (message: string) => {
          cleanup()
          script.dataset.kiliScriptState = 'failed'
          if (!existing) script.remove()
          requests.delete(absoluteUrl)
          reject(new Error(message))
        }
        const onError = () => fail('Unable to load script')
        const timer = setTimeout(() => fail('Script load timed out'), timeoutMs)
        script.addEventListener('load', onLoad)
        script.addEventListener('error', onError)
        if (!existing) {
          script.src = absoluteUrl
          if (options.id) script.id = options.id
          if (options.async !== undefined) script.async = options.async
          if (options.defer !== undefined) script.defer = options.defer
          script.dataset.kiliScriptState = 'loading'
          document.body.appendChild(script)
        }
      })
    }
  }

  const result = request.loaded ? Promise.resolve('existed') : request.promise
  if (!options.ready) return result
  const ready = options.ready
  const entry = request
  return result.then(value => new Promise((resolve, reject) => {
    const check = () => {
      if (isReady(ready)) resolve(value)
      else if (Date.now() - started >= timeoutMs) {
        // A load event does not prove the SDK executed successfully. Let a retry
        // fetch an owned failed script, without deleting a newer request or a
        // script inserted by another part of the application.
        if (requests.get(absoluteUrl) === entry) {
          requests.delete(absoluteUrl)
          if (entry.owned) {
            entry.script.dataset.kiliScriptState = 'failed'
            entry.script.remove()
          }
        }
        reject(new Error('Script API ready timed out'))
      }
      else setTimeout(check, 25)
    }
    check()
  }))
}
